<script setup>
import { computed, ref, watch } from "vue";
import store from "@/store/index";
import { useRouter } from "@/utils/vue_helpers";
import { routes } from "@/router/routes";
import {
  sessionState,
  formatCountdown,
  hideSessionWarning,
  refreshSessionWithServer,
} from "@/utils/session_timeout";
import { clearLocalAuthSession } from "@/utils/auth_session";

const { router } = useRouter();
const refreshing = ref(false);

const isAuthenticated = computed(() => store.getters.isAuthenticated);

const showWarning = computed(() => {
  return (
    isAuthenticated.value &&
    !sessionState.warningHidden &&
    sessionState.secondsRemaining <= 30 &&
    sessionState.secondsRemaining > 0
  );
});

const countdownLabel = computed(() =>
  formatCountdown(sessionState.secondsRemaining)
);

watch(
  () => sessionState.secondsRemaining,
  (remaining) => {
    if (remaining === 0 && isAuthenticated.value) {
      clearLocalAuthSession();
      router.value.push(routes.login);
    }
  }
);

async function stayLoggedIn() {
  refreshing.value = true;
  try {
    await refreshSessionWithServer();
  } catch (error) {
    clearLocalAuthSession();
    router.value.push(routes.login);
  } finally {
    refreshing.value = false;
  }
}

function onHide() {
  hideSessionWarning();
}
</script>

<template>
  <v-dialog
    :value="showWarning"
    persistent
    max-width="480"
  >
    <v-card rounded="lg" class="pa-4" color="warning lighten-4">
      <div class="d-flex flex-column flex-sm-row align-sm-center" :style="{ gap: '12px' }">
        <div class="d-flex align-center flex-grow-1">
          <v-icon color="warning darken-3" class="mr-3">mdi-timer-sand</v-icon>
          <div>
            <div class="text-subtitle-2 font-weight-bold warning--text text--darken-4">
              Session expiring soon
            </div>
            <div class="text-body-2">
              You will be logged out in
              <span class="font-weight-bold">{{ countdownLabel }}</span>
              due to inactivity.
            </div>
          </div>
        </div>
        <div class="d-flex align-center justify-center flex-shrink-0" :style="{ gap: '8px' }">
          <v-btn small text class="text-none" @click="onHide">
            Hide
          </v-btn>
          <v-btn
            small
            depressed
            color="primary"
            class="text-none white--text"
            :loading="refreshing"
            @click="stayLoggedIn"
          >
            Stay logged in
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>
